/* eslint-disable react-hooks/exhaustive-deps */
import React, { RefObject, useContext, useEffect, useState } from 'react';
import { isEqual, sortBy } from 'lodash';
import { getCellRef } from 'components/FeaturedSpreadsheet/utilities/utilities';
import FeaturedSpreadsheetContext, { FeaturedSpreadsheetContextType } from '../../context/FeaturedSpreadsheetContext';

const CONTENT_COLUMNS_TABLE = '#spreadsheet-table__content-columns table';
const CONTENT_COLUMNS_CELLS = `${CONTENT_COLUMNS_TABLE} > tbody > tr > td:not(.legend)`;
const COLUMN_REARRANGE_BUTTONS = '#spreadsheet-table__content-columns .column-rearrange';

const DRAGGING_COLUMN = 'dragging-column';
const DRAGGING_COLUMN_CONTAINER = 'dragging-column-container';
const DRAGGED_CELL = 'dragged-cell';
const LTR_LEFT = 'ltr-left';
const LTR_RIGHT = 'ltr-right';
const RTL_LEFT = 'rtl-left';
const RTL_RIGHT = 'rtl-right';

interface DraggableColumnsProps {
  tableRef: RefObject<HTMLElement>;
  children: React.ReactElement;
}

const DraggableColumns: React.FC<DraggableColumnsProps> = ({ tableRef, children }) => {
  const {
    cells,
    columns,
    setColumns,
    allowReorderColumns,
    reverseParser,
    disabled,
    pageFieldAttributes,
    selectedCell,
  } = useContext<FeaturedSpreadsheetContextType>(FeaturedSpreadsheetContext);

  const [draggingCell, setDraggingCell] = useState<HTMLElement | null>(null);
  const [draggingCells, setDraggingCells] = useState<HTMLElement[] | null>(null);
  const [draggingOverCell, setDraggingOverCell] = useState<HTMLElement | null>(null);

  const setCustomPreview = (e: DragEvent, draggingCell: HTMLElement): void => {
    const previewSource = document.querySelector(CONTENT_COLUMNS_TABLE);
    const previewTable = previewSource?.cloneNode(false) as HTMLTableElement | undefined;
    if (!previewTable) {
      return;
    }

    // Clone the table without its childrens
    const newTbody = document.createElement('tbody');
    const newRow = document.createElement('tr');

    // Add some new styles to the table
    previewTable.classList.add(DRAGGING_COLUMN);
    newTbody.appendChild(newRow);
    previewTable.appendChild(newTbody);

    // Filter all the cells outside the column dragged
    const tempDraggingCells = [
      ...document.querySelectorAll(`tr.visible > td[data-column-ref="${draggingCell.dataset.columnRef}"]:not(.legend)`),
    ] as HTMLElement[];
    setDraggingCells(tempDraggingCells);

    tempDraggingCells.forEach(cell => {
      // Insert cells in the new filtered table
      const newCell = cell.cloneNode(true) as HTMLElement;
      newCell.style.display = 'block';
      previewTable.querySelector('tr')?.appendChild(newCell);

      // Adding styles to the originall cells while draging
      cell.classList.add(DRAGGED_CELL);
    });

    // Set the custom preview
    const previewContainer = document.createElement('div');
    previewContainer.classList.add(DRAGGING_COLUMN_CONTAINER);
    previewContainer.appendChild(previewTable);

    document.body.appendChild(previewContainer);
    e.dataTransfer?.setDragImage(previewTable, 0, 0);
  };

  const getDropSide = (e: MouseEvent): string | null => {
    if (!draggingOverCell) {
      return null;
    }
    if (draggingCell) {
      const startingPosition = draggingCell.getBoundingClientRect().left;
      const hoveredCell = draggingOverCell.getBoundingClientRect();
      const mousePosition = e.clientX;
      const cellsCenter = hoveredCell.left + hoveredCell.width / 2;

      // Dragging from left to right
      if (startingPosition < mousePosition) {
        return mousePosition < cellsCenter ? LTR_LEFT : LTR_RIGHT;
      }

      // Dragging from right to left
      if (startingPosition > mousePosition) {
        return mousePosition > cellsCenter ? RTL_RIGHT : RTL_LEFT;
      }
    }
    return null;
  };

  const showDropIndicator = (e: MouseEvent): void => {
    const dropSide = getDropSide(e);
    const hoveredCell = (e.target as HTMLElement).closest('tr.visible > td') as HTMLElement;
    setDraggingOverCell(hoveredCell);

    if (dropSide) {
      // Get the column legend of the dragged over cell
      const hoveredCellColumnLegend = hoveredCell.dataset.columnLegend ?? '';

      const hoveredColumnCells = [
        ...document.querySelectorAll(`tr > td[data-column-legend="${hoveredCellColumnLegend}"]`),
      ] as HTMLElement[];

      // Filter the draggable cells by this column legend
      hoveredColumnCells.forEach(cell => {
        cell.classList.remove(LTR_LEFT, LTR_RIGHT, RTL_LEFT, RTL_RIGHT);
        cell.classList.add(dropSide);
      });
    }
  };

  const hideDropIndicator = (): void => {
    document.querySelectorAll(CONTENT_COLUMNS_CELLS).forEach(cell => {
      (cell as HTMLElement).classList.remove(LTR_LEFT, LTR_RIGHT, RTL_LEFT, RTL_RIGHT);
    });
  };

  const cleanDraggingCellsStyles = (): void => {
    const tmpDraggingCells = Array.isArray(draggingCells) ? [...draggingCells] : [];
    tmpDraggingCells.forEach(cell => cell.classList.remove(DRAGGED_CELL));
  };

  const getDragColumnRef = (column: { columnRef?: unknown; security_ref?: unknown } | null | undefined): string => {
    if (!column) {
      return '';
    }
    const ref = column.columnRef ?? column.security_ref;
    return ref == null ? '' : String(ref);
  };

  const rearrangeColumns = (e: MouseEvent): void => {
    const originColumnRef = draggingCell?.dataset?.columnRef ?? '';
    const destinationColumnRef = draggingOverCell?.dataset.columnRef ?? '';
    if (reverseParser) {
      const updatedColumns = sortBy(reverseParser(cells, columns, pageFieldAttributes), ['order']);
      const originColumnIndex = updatedColumns.findIndex(column => {
        const columnRef = getDragColumnRef(column);
        return columnRef !== '' && columnRef === String(originColumnRef);
      });

      if (originColumnIndex === -1) {
        return;
      }

      const destinationColumnIndex = updatedColumns.findIndex(column => {
        const columnRef = getDragColumnRef(column);
        return columnRef !== '' && columnRef === String(destinationColumnRef);
      });
  
      // Cut the origin column from the updated list
      const originColumn = { ...updatedColumns[originColumnIndex] };
  
      updatedColumns.splice(originColumnIndex, 1);
  
      // Paste the origin column in the updated list using the new position
      switch (getDropSide(e)) {
        case LTR_LEFT:
          updatedColumns.splice(destinationColumnIndex - 1, 0, originColumn);
          break;
        case RTL_RIGHT:
          updatedColumns.splice(destinationColumnIndex + 1, 0, originColumn);
          break;
        default:
          updatedColumns.splice(destinationColumnIndex, 0, originColumn);
          break;
      }
  
      // Change only if the order is different
      const oldOrder = columns.map(column => column.order);
      const newOrder = updatedColumns.map(column => column.order);
      const hasOrder = columns.reduce((a, b) => Number(a) + Number(b.order ?? 0), 0) > 0;
  
      if (isEqual(oldOrder, newOrder) && hasOrder) {
        cleanDraggingCellsStyles();
      } else {
        // Update the order prop
        const sortedColumns = updatedColumns.map((column, columnIndex) => ({
          ...column,
          order: columnIndex,
        }));
        if (setColumns) {
          setColumns(sortedColumns);
        }
      }
    }
  };

  const draggStartHandler = (event: DragEvent): void => {
    if (!selectedCell) {
      return;
    }
    
    const { cellInfo } = selectedCell;
    if (!cellInfo.key) {
      return;
    }
    const tempDraggingCell = getCellRef(tableRef, cellInfo.key) as HTMLElement;
    setDraggingCell(tempDraggingCell);
    setCustomPreview(event, tempDraggingCell);
  };

  const draggOverHandler = (e: DragEvent): void => {
    e.preventDefault();
    hideDropIndicator();
    showDropIndicator(e);
  };

  const draggLeaveHandler = (): void => {
    hideDropIndicator();
  };

  const draggEndHandler = (e: DragEvent): void => {
    hideDropIndicator();
    const container = document.querySelector('.dragging-column-container');
    if (container) {
      document.body.removeChild(container);
    }
    if (draggingOverCell) {
      rearrangeColumns(e);
    }
  };

  useEffect(() => {
    if (columns?.length && draggingCells) {
      cleanDraggingCellsStyles();
    }
  }, [columns]);

  const buttonDragOverHandler = (e: Event): void => {
    e.preventDefault();
    const dragEvent = e as DragEvent;
    const hoveredCell = (dragEvent.target as HTMLElement).closest('tr.visible > td') as HTMLElement;
    setDraggingOverCell(hoveredCell);
  };

  const cellDragStartHandler = (e: Event): void => {
    e.preventDefault();
  };

  useEffect(() => {
    if (!allowReorderColumns || disabled) {
      return undefined;
    }

    let boundButtons: HTMLElement[] = [];
    let boundCells: HTMLElement[] = [];
    let rafId = 0;
    let cancelled = false;

    const unbind = () => {
      boundButtons.forEach(button => {
        button.setAttribute('draggable', 'false');
        button.removeEventListener('dragstart', draggStartHandler as EventListener);
        button.removeEventListener('dragover', buttonDragOverHandler);
        button.removeEventListener('dragend', draggEndHandler as EventListener);
      });
      boundCells.forEach(cell => {
        cell.setAttribute('draggable', 'false');
        cell.removeEventListener('dragstart', cellDragStartHandler);
        cell.removeEventListener('dragover', draggOverHandler as EventListener);
        cell.removeEventListener('dragleave', draggLeaveHandler as EventListener);
      });
      boundButtons = [];
      boundCells = [];
    };

    const bind = (): boolean => {
      if (cancelled) {
        return false;
      }
      unbind();
      const buttons = [...document.querySelectorAll(COLUMN_REARRANGE_BUTTONS)] as HTMLElement[];
      const tableCells = [...document.querySelectorAll(CONTENT_COLUMNS_CELLS)] as HTMLElement[];
      if (!buttons.length || !tableCells.length) {
        return false;
      }
      buttons.forEach(button => {
        button.setAttribute('draggable', 'true');
        button.addEventListener('dragstart', draggStartHandler as EventListener);
        button.addEventListener('dragover', buttonDragOverHandler);
        button.addEventListener('dragend', draggEndHandler as EventListener);
      });
      tableCells.forEach(cell => {
        cell.setAttribute('draggable', 'true');
        cell.addEventListener('dragstart', cellDragStartHandler);
        cell.addEventListener('dragover', draggOverHandler as EventListener);
        cell.addEventListener('dragleave', draggLeaveHandler as EventListener);
      });
      boundButtons = buttons;
      boundCells = tableCells;
      return true;
    };

    // ColumnIndicator paints after this effect; retry so first-load drag binds
    // without waiting for a later columns rebuild (add security, etc.).
    if (!bind()) {
      rafId = requestAnimationFrame(() => {
        if (!bind()) {
          rafId = requestAnimationFrame(() => bind());
        }
      });
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      unbind();
    };
  }, [columns, draggingCell, draggingOverCell, disabled, selectedCell, allowReorderColumns]);

  return <>{children}</>;
};

export default DraggableColumns;